"use client";

import Link from "next/link";
import {
  ArrowUpRight,
  BookOpen,
  Building2,
  GraduationCap,
  Mail,
  ShieldCheck,
} from "lucide-react";

const studentLinks = [
  { label: "Create an account", href: "/signup" },
  { label: "Student login", href: "/login" },
  { label: "Explore exams", href: "/exams" },
  { label: "Platform features", href: "/features" },
];

const instituteLinks = [
  { label: "Institute platform", href: "/institutes" },
  { label: "About TCD", href: "/about" },
  { label: "Contact support", href: "/contact" },
];

const legalLinks = [
  { label: "Privacy policy", href: "/privacy-policy" },
  { label: "Terms & conditions", href: "/terms-and-conditions" },
  { label: "Refund policy", href: "/refund-policy" },
];

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <Link
        href={href}
        className="group inline-flex items-center gap-1.5 text-sm text-white/65 transition-colors duration-200 hover:text-brand-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050B1A]"
      >
        {children}
        <ArrowUpRight
          className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}

export default function Footer() {
  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-[#050B1A] text-white">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/40 to-transparent" aria-hidden="true" />

      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr] lg:gap-12">
          <div>
            <Link href="/" className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70">
              <span className="text-xl font-black tracking-tight text-white sm:text-2xl">
                THE CONCLUSION <span className="text-brand-gold">DAILY</span>
              </span>
            </Link>

            <p className="mt-3 text-sm font-semibold tracking-[0.2em] text-brand-gold">
              HOPE &amp; FAITH
            </p>

            <p className="mt-5 max-w-sm text-sm leading-7 text-white/65">
              A competitive learning ecosystem for students and an online
              examination platform for educators and institutes.
            </p>

            <a
              href="mailto:support@theconclusiondaily.com"
              className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm text-white/75 transition hover:border-brand-gold/30 hover:text-brand-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70"
            >
              <Mail className="h-4 w-4 text-brand-gold" aria-hidden="true" />
              support@theconclusiondaily.com
            </a>
          </div>

          <nav aria-label="Student links">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-white">
              <GraduationCap className="h-4 w-4 text-brand-gold" aria-hidden="true" />
              Students
            </h3>
            <ul className="mt-5 space-y-3.5">
              {studentLinks.map((link) => (
                <FooterLink key={link.href} href={link.href}>
                  {link.label}
                </FooterLink>
              ))}
            </ul>
          </nav>

          <nav aria-label="Institute and company links">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-white">
              <Building2 className="h-4 w-4 text-brand-gold" aria-hidden="true" />
              Institutes
            </h3>
            <ul className="mt-5 space-y-3.5">
              {instituteLinks.map((link) => (
                <FooterLink key={link.href} href={link.href}>
                  {link.label}
                </FooterLink>
              ))}
            </ul>
            <div className="mt-7 flex items-center gap-2 text-xs leading-5 text-white/45">
              <BookOpen className="h-4 w-4 shrink-0 text-brand-gold/80" aria-hidden="true" />
              Practice, assess, and improve.
            </div>
          </nav>

          <nav aria-label="Legal links">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-white">
              <ShieldCheck className="h-4 w-4 text-brand-gold" aria-hidden="true" />
              Policies
            </h3>
            <ul className="mt-5 space-y-3.5">
              {legalLinks.map((link) => (
                <FooterLink key={link.href} href={link.href}>
                  {link.label}
                </FooterLink>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs leading-5 text-white/45 sm:mt-14 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 THE CONCLUSION DAILY. All rights reserved.</p>
          <p className="max-w-xl sm:text-right">
            Learning outcomes and competition features depend on participation,
            availability, and the applicable platform rules.
          </p>
        </div>
      </div>
    </footer>
  );
}
